import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { BookOpen, LogOut, History, ClipboardCheck, PenTool, Shield, BarChart3, Library, Crown } from 'lucide-react';
import { GradingForm, SUBJECTS } from '../components/GradingForm';
import { FeedbackResult, FeedbackPlaceholder } from '../components/FeedbackDisplay';
import { FeedbackSkeleton } from '../components/FeedbackSkeleton';
import { HistoryPanel } from '../components/HistoryPanel';
import CreateTab from '../components/CreateTab';
import PerformanceTab from '../components/PerformanceTab';
import QuestionBankTab from '../components/QuestionBankTab';
import UpgradeGate from '../components/UpgradeGate';
import StudentClassesCard from '../components/StudentClassesCard';
import StudentAssignmentsCard from '../components/StudentAssignmentsCard';

export default function DashboardPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Tab state
    const [activeTab, setActiveTab] = useState('mark');
    
    // Mark tab form state
    const [subject, setSubject] = useState('business');
    const [level, setLevel] = useState('IAL');
    const [question, setQuestion] = useState('');
    const [studentAnswer, setStudentAnswer] = useState('');
    const [answerImages, setAnswerImages] = useState([]);
    const [questionImage, setQuestionImage] = useState(null);
    const [subjectDropdownOpen, setSubjectDropdownOpen] = useState(false);
    
    // UI state
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [error, setError] = useState('');
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    // Active assignment context (set when a student clicks an assignment card)
    const [activeAssignment, setActiveAssignment] = useState(null);

    const fetchHistory = useCallback(async () => {
        try {
            const response = await api.get('/api/feedback/history');
            setHistory(response.data);
        } catch (err) {
            console.error('Failed to fetch history:', err);
        }
    }, []);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    // Teacher accounts route to /teacher dashboard — placed AFTER hooks
    // to avoid violating React's Rules of Hooks. Admin can use both routes.
    if (user && user !== false && user.role === 'teacher') {
        return <Navigate to="/teacher" replace />;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFeedback(null);
        setLoading(true);
        try {
            const requestData = {
                subject: SUBJECTS.find(s => s.id === subject)?.name || subject,
                level,
                question,
                student_answer: studentAnswer,
                mark_scheme: ''
            };
            if (answerImages.length > 0) requestData.answer_image_base64 = answerImages;
            if (questionImage) requestData.question_image_base64 = questionImage;
            // Attach class-assignment context so the teacher sees this submission
            // against the assignment.
            if (activeAssignment) {
                requestData.assignment_id = activeAssignment.id;
                requestData.paper_year = activeAssignment.paper_year || '';
                requestData.series = activeAssignment.series || '';
                requestData.question_number = activeAssignment.question_number || '';
                requestData.unit = activeAssignment.unit || '';
                if (activeAssignment.mark_scheme) requestData.mark_scheme = activeAssignment.mark_scheme;
            }
            const response = await api.post('/api/feedback', requestData);
            setFeedback(response.data);
            // Clear assignment context after a successful submission
            if (activeAssignment) setActiveAssignment(null);
            fetchHistory();
        } catch (err) {
            const HTTP_UNAUTHORIZED = 401;
            if (err.response?.status === HTTP_UNAUTHORIZED) {
                navigate('/login');
            } else {
                setError(err.response?.data?.detail || 'Failed to get feedback. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const openAssignment = (a) => {
        // Find the matching subject id
        const matchedSubject = SUBJECTS.find((s) => s.name === a.subject);
        if (matchedSubject) setSubject(matchedSubject.id);
        if (a.level) setLevel(a.level);
        setQuestion(a.question_text || `Paper ${a.paper_year} ${a.series} · Q${a.question_number}`);
        setStudentAnswer('');
        setAnswerImages([]);
        setQuestionImage(null);
        setFeedback(null);
        setError('');
        setActiveAssignment(a);
        setActiveTab('mark');
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const clearForm = () => {
        setQuestion('');
        setStudentAnswer('');
        setAnswerImages([]);
        setQuestionImage(null);
        setFeedback(null);
        setError('');
    };

    const loadFromHistory = (item) => {
        const matchedSubject = SUBJECTS.find(s => s.name === item.subject);
        setSubject(matchedSubject?.id || 'business');
        setQuestion(item.question);
        setStudentAnswer(item.student_answer);
        setAnswerImages([]);
        setQuestionImage(null);
        setFeedback(item);
        setShowHistory(false);
        setActiveTab('mark');
    };

    return (
        <div className="min-h-screen bg-[#F8F7FC]">
            {/* Header */}
            <header className="border-b-2 border-zinc-950 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                            <div className="w-10 h-10 rounded-lg bg-[#A78BFA] border-2 border-zinc-950 flex items-center justify-center">
                                <BookOpen className="w-5 h-5 text-white" />
                            </div>
                            <h1 className="text-xl font-black tracking-tight hidden sm:block">A-Level AI Grader</h1>
                        </a>
                        <div className="flex items-center gap-4">
                            {user?.trial?.is_trialing && (
                                <button
                                    onClick={() => navigate('/pricing')}
                                    className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 border-2 border-amber-200 text-amber-900 text-[11px] font-black uppercase tracking-wide hover:bg-amber-100 transition-colors"
                                    data-testid="trial-status-badge"
                                    title="Click to keep Pro after trial"
                                >
                                    <Crown className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                                    Pro Trial · {user.trial.trial_days_left}d left
                                </button>
                            )}
                            {user?.role === 'admin' && (
                                <button
                                    onClick={() => navigate('/teacher')}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border-2 border-[#7C3AED] text-[#7C3AED] hover:bg-[#F4ECFF] text-xs font-bold transition-colors"
                                    data-testid="switch-to-teacher"
                                    title="Switch to Teacher view"
                                >
                                    Teacher view →
                                </button>
                            )}
                            {user?.email === 'gonzalezzb29@gmail.com' && (
                                <button
                                    onClick={() => navigate('/admin')}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-bold transition-colors shadow-sm"
                                    data-testid="admin-link"
                                    title="Open Admin Panel"
                                >
                                    <Shield className="w-3.5 h-3.5" />
                                    <span className="hidden sm:inline">Admin</span>
                                </button>
                            )}
                            <button
                                onClick={() => setShowHistory(!showHistory)}
                                className="brutalist-btn-secondary flex items-center gap-2 py-2 px-4"
                                data-testid="history-toggle-button"
                            >
                                <History className="w-4 h-4" />
                                <span className="hidden sm:inline">History</span>
                            </button>
                            <span className="text-sm text-zinc-600 hidden md:inline">
                                Hi, <span className="font-semibold">{user?.name}</span>
                            </span>
                            <button onClick={handleLogout} className="p-2 hover:bg-zinc-100 rounded-lg transition-colors" data-testid="logout-button">
                                <LogOut className="w-5 h-5 text-zinc-600" />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Tab Navigation */}
            <div className="border-b border-zinc-200 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex gap-0">
                        <button
                            onClick={() => setActiveTab('mark')}
                            className={`flex items-center gap-2 px-6 py-3.5 text-sm font-bold border-b-3 transition-colors ${
                                activeTab === 'mark'
                                    ? 'border-[#A78BFA] text-[#A78BFA]'
                                    : 'border-transparent text-zinc-500 hover:text-zinc-700'
                            }`}
                            style={{ borderBottomWidth: '3px' }}
                            data-testid="tab-mark"
                        >
                            <ClipboardCheck className="w-4 h-4" />
                            Mark
                        </button>
                        <button
                            onClick={() => setActiveTab('create')}
                            className={`flex items-center gap-2 px-6 py-3.5 text-sm font-bold border-b-3 transition-colors ${
                                activeTab === 'create'
                                    ? 'border-[#A78BFA] text-[#A78BFA]'
                                    : 'border-transparent text-zinc-500 hover:text-zinc-700'
                            }`}
                            style={{ borderBottomWidth: '3px' }}
                            data-testid="tab-create"
                        >
                            <PenTool className="w-4 h-4" />
                            Create
                        </button>
                        <button
                            onClick={() => setActiveTab('performance')}
                            className={`flex items-center gap-2 px-6 py-3.5 text-sm font-bold border-b-3 transition-colors ${
                                activeTab === 'performance'
                                    ? 'border-[#A78BFA] text-[#A78BFA]'
                                    : 'border-transparent text-zinc-500 hover:text-zinc-700'
                            }`}
                            style={{ borderBottomWidth: '3px' }}
                            data-testid="tab-performance"
                        >
                            <BarChart3 className="w-4 h-4" />
                            My Performance
                        </button>
                        <button
                            onClick={() => setActiveTab('question-bank')}
                            className={`flex items-center gap-2 px-6 py-3.5 text-sm font-bold border-b-3 transition-colors ${
                                activeTab === 'question-bank'
                                    ? 'border-[#A78BFA] text-[#A78BFA]'
                                    : 'border-transparent text-zinc-500 hover:text-zinc-700'
                            }`}
                            style={{ borderBottomWidth: '3px' }}
                            data-testid="tab-question-bank"
                        >
                            <Library className="w-4 h-4" />
                            Question Bank
                        </button>
                    </div>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Student class enrollments */}
                <StudentClassesCard />

                {/* Class assignments */}
                <StudentAssignmentsCard onOpenInMark={openAssignment} />

                {/* Trial CTA banner for eligible free users */}
                {user?.subscription_tier === 'free' && user?.trial?.trial_eligible && (
                    <button
                        onClick={() => navigate('/pricing')}
                        className="w-full mb-6 rounded-2xl p-4 sm:p-5 bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] text-white shadow-[0_14px_35px_-15px_rgba(124,58,237,0.55)] flex flex-col sm:flex-row items-center gap-3 sm:gap-5 text-left hover:shadow-[0_18px_45px_-15px_rgba(124,58,237,0.7)] transition-shadow"
                        data-testid="dashboard-trial-cta"
                    >
                        <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                            <Crown className="w-5 h-5 fill-amber-300 text-amber-300" />
                        </div>
                        <div className="flex-1">
                            <div className="font-black text-base sm:text-lg tracking-tight">Try Pro free for {user?.trial?.trial_days_total || 7} days</div>
                            <div className="text-xs sm:text-sm text-white/85 mt-0.5">Unlock unlimited gradings & My Performance — no credit card required.</div>
                        </div>
                        <span className="bg-white text-[#7C3AED] font-black text-xs px-4 py-2.5 rounded-xl whitespace-nowrap">Start free trial →</span>
                    </button>
                )}

                {/* History Panel */}
                <HistoryPanel
                    history={history}
                    showHistory={showHistory}
                    onClose={() => setShowHistory(false)}
                    onLoadItem={loadFromHistory}
                />

                {/* Mark Tab */}
                {activeTab === 'mark' && (
                    <div className="fade-in">
                        {activeAssignment && (
                            <div className="mb-6 p-4 rounded-2xl bg-[#7C3AED] text-white flex items-center gap-4" data-testid="active-assignment-banner">
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-white/70">Answering class assignment</div>
                                    <div className="font-black text-sm truncate">{activeAssignment.title || `Paper ${activeAssignment.paper_year || ''} ${activeAssignment.series || ''} · Q${activeAssignment.question_number || ''}`}</div>
                                    <div className="text-xs text-white/80 truncate">{activeAssignment.classroom_name} · {activeAssignment.subject}</div>
                                </div>
                                <button onClick={() => setActiveAssignment(null)} className="px-3 py-1.5 rounded-lg bg-white/15 text-white text-xs font-bold hover:bg-white/25" data-testid="clear-active-assignment">
                                    Cancel
                                </button>
                            </div>
                        )}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                            <GradingForm
                                subject={subject}
                                setSubject={setSubject}
                                level={level}
                                setLevel={setLevel}
                                question={question}
                                setQuestion={setQuestion}
                                studentAnswer={studentAnswer}
                                setStudentAnswer={setStudentAnswer}
                                answerImages={answerImages}
                                setAnswerImages={setAnswerImages}
                                questionImage={questionImage}
                                setQuestionImage={setQuestionImage}
                                loading={loading}
                                error={error}
                                onSubmit={handleSubmit}
                                onClear={clearForm}
                                subjectDropdownOpen={subjectDropdownOpen}
                                setSubjectDropdownOpen={setSubjectDropdownOpen}
                            />
                        </div>
                        <div>
                            {loading ? (
                                <FeedbackSkeleton />
                            ) : feedback ? (
                                <FeedbackResult feedback={feedback} />
                            ) : (
                                <FeedbackPlaceholder />
                            )}
                        </div>
                    </div>
                </div>
                )}

                {/* Create Tab */}
                {activeTab === 'create' && (
                    <div className="fade-in">
                        <CreateTab />
                    </div>
                )}

                {/* Performance Tab — gated for free users */}
                {activeTab === 'performance' && (
                    user?.limits?.performance === false ? (
                        <UpgradeGate
                            title="Unlock My Performance"
                            description="Track your progress, see weak topics, and get personalised AI insights. Available on Essential and Pro plans."
                            ctaText="See plans"
                        />
                    ) : (
                        <PerformanceTab
                            onPracticeTopic={(item) => {
                                const matchedSubject = SUBJECTS.find((s) => s.name === item.subject);
                                if (matchedSubject) setSubject(matchedSubject.id);
                                setActiveTab('mark');
                            }}
                        />
                    )
                )}

                {/* Question Bank Tab */}
                {activeTab === 'question-bank' && <QuestionBankTab />}
            </main>
        </div>
    );
}
